import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import apiClient from '../../../services/apiClient'
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CContainer,
  CRow,
  CNavbarBrand,
  CNavbar,
  CImage,
  CBadge,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import {
  cilPhone,
  cilCalendarCheck,
  cilXCircle,
  cilList,
  cilArrowCircleLeft,
  cilCart,
} from '@coreui/icons'
import ownLogo from 'src/assets/brand/ic_launcher.png'
import reminderHero from 'src/assets/images/ecosystem.jpeg'
import { useTranslation } from 'react-i18next'
import { buildApiUrl } from '../../../utils/getConfiguration'
import BadgeComponent from '../../widgets/BadgeComponent'

const RemindersExample = () => {
  const navigate = useNavigate()
  const [recommendations, setRecommendations] = useState([])

  useEffect(() => {
    const fetchRecommendationsData = async () => {
      try {
        const grp_ID = localStorage.getItem('grp_ID')
        const response = await apiClient.get(buildApiUrl('remindersGroup', { grp_ID }))
        setRecommendations(response.data)
      } catch (error) {
        console.error('Error fetching recommendations', error)
      }
    }

    fetchRecommendationsData()
  }, [])

  const { t } = useTranslation()
  const electricitySteps = t('login.personal.stepsCard.steps', { returnObjects: true }) || []
  const areaKeys = useMemo(
    () => [
      'housing',
      'swimmingPool',
      'garden',
      'pets',
      'health',
      'car',
      'children',
      'family',
      'partner',
      'devices',
      'cooking',
      'work',
      'payments',
      'training',
      'fun',
      'hobby',
      'transport',
    ],
    [],
  )

  const actionButtons = useMemo(
    () => [
      { key: 'calendar', icon: cilCalendarCheck },
      { key: 'phone', icon: cilPhone },
      { key: 'cart', icon: cilCart },
      { key: 'list', icon: cilList },
      { key: 'dismiss', icon: cilXCircle },
    ],
    [],
  )

  const formatDescription = (primary, secondary) => {
    if (primary && String(primary).trim().length > 0) {
      return primary
    }
    if (secondary && String(secondary).trim().length > 0) {
      return secondary
    }
    return t('login.remindersExample.fallbackDescription', 'We will share more details soon.')
  }

  const formatRisk = (damage) => {
    if (!damage || String(damage) === 'null') {
      return t('login.remindersExample.fallbackValue')
    }
    return `${damage}$`
  }

  const formatMonth = (expectedDate) => {
    if (String(expectedDate) === '0') {
      return t('login.remindersExample.fallbackInit')
    }
    return expectedDate
  }

  return (
    <div className="reminders-page min-vh-100 d-flex flex-column">
      <CContainer>
        <CNavbar expand="lg" className="reminders-navbar border-0 shadow-sm mb-4">
          <div className="d-flex align-items-center gap-3 w-100">
            <CButton
              color="primary"
              className="reminders-back-btn"
              onClick={() => window.history.back()}
              aria-label={t('navigation.back', 'Go back')}
            >
              <CIcon icon={cilArrowCircleLeft} size="lg" />
            </CButton>
            <CNavbarBrand href="#" className="reminders-navbar__brand d-flex align-items-center">
              <img
                src={ownLogo}
                alt={t('login.remindersExample.brandAlt', 'Reminder AI logo')}
                height={44}
                className="me-2"
              />
              <span className="fw-semibold">{t('login.brand.name')}</span>
            </CNavbarBrand>
          </div>
        </CNavbar>

        <CRow className="justify-content-center g-4">
          <CCol lg={10}>
            <CCard className="reminders-hero-card border-0 shadow-lg overflow-hidden">
              <div className="reminders-hero-card__media">
                <CImage
                  src={reminderHero}
                  alt={t('login.remindersExample.heroImageAlt', 'Reminder preview')}
                />
              </div>
              <CCardBody className="reminders-hero-card__body text-white">
                <div className="reminders-hero-card__intro">
                  <span className="reminders-pill">
                    {t('login.remindersExample.heroPill', 'Smart reminders')}
                  </span>
                  <h1 className="reminders-hero-card__title">
                    {t('login.personal.toolsCard.heading')}
                  </h1>
                  <p className="reminders-hero-card__lead">
                    {t('login.personal.toolsCard.description')}
                  </p>
                  <CButton
                    color="light"
                    className="reminders-hero-card__cta"
                    onClick={() => navigate('/register')}
                  >
                    {t('login.app.button')}
                  </CButton>
                </div>
                <div className="reminders-example-area-panel">
                  <h2 className="reminders-section-title">
                    {t('login.personal.toolsCard.areaHeading')}
                  </h2>
                  <div className="reminders-area-grid">
                    {areaKeys.map((areaKey) => {
                      const isPrimary = areaKey === 'housing'
                      return (
                        <CButton
                          key={areaKey}
                          color="light"
                          variant={isPrimary ? undefined : 'outline'}
                          className={`reminders-area-btn${isPrimary ? ' reminders-area-btn--active' : ''}`}
                          size="sm"
                          disabled={!isPrimary}
                          onClick={() => {
                            if (isPrimary) {
                              navigate('/remindersexample')
                            }
                          }}
                        >
                          {t(`areas.${areaKey}`)}
                        </CButton>
                      )
                    })}
                  </div>
                </div>
              </CCardBody>
            </CCard>
          </CCol>

          <CCol lg={10}>
            <CCard className="reminders-catalog-card border-0 shadow-sm">
              <CCardBody>
                <div className="reminders-catalog-header">
                  <div>
                    <span className="reminders-pill reminders-pill--muted">
                      {t('login.remindersExample.catalogAccent', "Today's highlights")}
                    </span>
                    <h2 className="reminders-catalog-title">
                      {t('login.remindersExample.sampleCardHeading')}
                    </h2>
                    <p className="reminders-catalog-subtitle">
                      {t('login.remindersExample.sampleCardDescription')}
                    </p>
                  </div>
                  <div className="reminders-catalog-summary">
                    <div className="reminders-steps">
                      {electricitySteps.map((step, index) => (
                        <div key={step} className="reminders-step">
                          <span className="reminders-step__index">{index + 1}</span>
                          <span>{step}</span>
                        </div>
                      ))}
                    </div>
                    <CButton
                      color="primary"
                      className="reminders-steps-cta"
                      onClick={() => navigate('/register')}
                    >
                      {t('login.personal.stepsCard.cta')}
                    </CButton>
                  </div>
                </div>

                <div className="reminders-catalog-list">
                  {recommendations.map((recommendation) => (
                    <div key={recommendation.reminder_ID} className="reminders-catalog-list__item">
                      <div className="reminders-catalog-list__media">
                        <CImage
                          rounded
                          src={recommendation.pic}
                          width={56}
                          height={56}
                          alt={recommendation.Title}
                          onClick={() => navigate('/register')}
                        />
                      </div>
                      <div className="reminders-catalog-list__content">
                        <div className="reminders-catalog-list__header">
                          <div>
                            <h3>{recommendation.Title}</h3>
                            <div className="reminders-catalog-list__meta">
                              <span className="reminders-catalog-list__badge">
                                {t('areas.housing')}
                              </span>
                              {recommendation.Prio && (
                                <BadgeComponent priority={recommendation.Prio} />
                              )}
                            </div>
                          </div>
                          <span className="reminders-catalog-list__points">
                            {recommendation.points} pts
                          </span>
                        </div>
                        <p>
                          {formatDescription(
                            recommendation.Description,
                            recommendation.description,
                          )}
                        </p>
                        <div className="reminders-catalog-list__attributes">
                          <div>
                            <span className="reminders-attribute__label">
                              {t('login.remindersExample.riskLabel')}
                            </span>
                            <span className="reminders-attribute__value">
                              {formatRisk(recommendation.Damage)}
                            </span>
                          </div>
                          <div>
                            <span className="reminders-attribute__label">
                              {t('login.remindersExample.monthLabel')}
                            </span>
                            <span className="reminders-attribute__value">
                              {formatMonth(recommendation.expecteddate)}
                            </span>
                          </div>
                          <div>
                            <span className="reminders-attribute__label">
                              {t('login.remindersExample.frequencyLabel', 'Frequency')}
                            </span>
                            <span className="reminders-attribute__value">
                              {recommendation.remind}
                            </span>
                          </div>
                        </div>
                        <div className="reminders-catalog-list__actions">
                          <span className="reminders-catalog-list__actions-label">
                            {t('login.remindersExample.actionsLabel')}
                          </span>
                          {actionButtons.map((action) => (
                            <CButton
                              key={`${recommendation.reminder_ID}-${action.key}`}
                              color="primary"
                              className="reminders-catalog-action-btn"
                              title={t(`login.remindersExample.actions.${action.key}`, action.key)}
                              aria-label={t(
                                `login.remindersExample.actions.${action.key}`,
                                action.key,
                              )}
                              onClick={() => navigate('/register')}
                            >
                              <CIcon icon={action.icon} size="lg" />
                            </CButton>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default RemindersExample
